'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo } from 'react';
import { v4 } from 'uuid';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import type { LinkCloudManufacturer } from '@//app/api/search/vehicles/link-cloud';
import type { Seo } from '@//app/api/seo/[id]';
import SearchLinkCloud from '@//app/search/[[...slug]]/containers/SearchLinkCloud';
import SearchResult from '@//app/search/[[...slug]]/containers/SearchResult';
import { PageContent } from '@//components/PageContent';
import { TextContent } from '@//components/TextContent';
import type { Market } from '@//config/domain-locale-map';
import useAddFilterHistory from '@//data/filter-history/useAddFilterHistory';
import useVehicleFacets from '@//data/search/vehicles/useVehicleFacets';
import useVehicles from '@//data/search/vehicles/useVehicles';
import useSearchSubscription from '@//data/subscriptions/search/useSearchSubscription';
import type { MarketMetadata } from '@//market';
import { markdownToHtml } from '@//utils/text';
import useEnrichedFacets from '@//utils/vehicles-search/useEnrichedFacets';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import useVehicleSearchParams from '@//utils/vehicles-search/useVehicleSearchParams';
import {
  extractQueryForVehicleSearch,
  getSubscribeableQueryForVehicleSearch,
  sanitizeParams,
} from '@//utils/vehicles-search/utils';
import ActiveFilters from '../ActiveFilters';
import useActiveFiltersSummaryLabel from '../ActiveFilters/useActiveFiltersSummaryLabel';
import FilterSort from '../FilterSort';
import { ManufacturerPackagingSuggestion } from '../ManufacturerPackagingSuggestion';
import Pagination from '../Pagination';
import SearchFilters from '../QuickFilters';
import { SearchControls } from '../SearchControls';
import { SearchHeader } from '../SearchHeader';
import SearchInput from '../SearchInput';
import SearchSubscription from '../SearchSubscription';
import { UtilityBar } from '../UtilityBar';
import useSearchSubscriptionHandler from '../useSearchSubscriptionHandler';
import useFilterHelper from './useFilterHelper';
import useResolvedSeo from './useResolvedSeo';
import useTrackSearch from './useTrackSearch';

interface InitialSeo {
  id: string;
  seo: Seo;
}

interface InitialDynamicSeo {
  title?: string;
  ingress?: string;
  metaTitle?: string;
  metaDescription?: string;
}

interface SearchProps {
  initialSeo?: InitialSeo;
  initialDynamicSeo?: InitialDynamicSeo | null;
  facets: ElasticVehiclesResponse;
  facetsByParams?: ElasticVehiclesResponse;
  itemPage: string;
  searchPage: string;
  currency: string;
  market: Market;
  marketMetadata: MarketMetadata;
  rootUrl: string;
  slug?: string[];
  linkCloudList: LinkCloudManufacturer[];
}

const SearchContainer = ({
  initialSeo,
  initialDynamicSeo,
  facets: initialFacets,
  facetsByParams: initialFacetsByParams,
  itemPage,
  searchPage,
  currency,
  market,
  marketMetadata,
  rootUrl,
  linkCloudList,
}: SearchProps) => {
  const pathname = usePathname();
  const t = useTranslations('pages.search');
  const tSearchSubscription = useTranslations('components.searchSubscription.search');
  const facetsQuery = useVehicleFacets({
    initialData: initialFacets,
  });
  const facets = facetsQuery.data;

  const addFilterHistory = useAddFilterHistory();

  const searchParams = useVehicleSearchParams(searchPage);
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);

  const searchSubscriptionId = searchParams.get('searchSubscriptionId') ?? undefined;
  const searchSubscriptionQuery = useSearchSubscription(searchSubscriptionId);
  const searchSubscription = searchSubscriptionQuery.data?.response;
  useSearchSubscriptionHandler({
    searchSubscriptionId,
    searchSubscriptionQuery,
    searchParams,
    rootUrl,
  });

  useEffect(() => {
    if (searchSubscriptionQuery.isSuccess && searchSubscription) {
      const tmpCurrent = new URLSearchParams(searchParams);
      tmpCurrent.sort();
      const tmpSearchSubscription = new URLSearchParams(searchSubscription.query);
      tmpSearchSubscription.set('searchSubscriptionId', searchSubscription.id);
      tmpSearchSubscription.sort();
      if (tmpCurrent.toString() !== tmpSearchSubscription.toString()) {
        updateUrl(sanitizeParams(tmpSearchSubscription));
      }
    }
  }, [searchSubscriptionQuery.isSuccess]);

  const vehicleSearchParams = useMemo(
    () => extractQueryForVehicleSearch(searchParams),
    [searchParams.toString()],
  );

  const vehiclesQuery = useVehicles(vehicleSearchParams);
  const { isPolling } = vehiclesQuery;
  const data = vehiclesQuery.data;
  const enrichedFacets = useEnrichedFacets({
    initial: facets,
    current: data ?? initialFacetsByParams,
    market,
  });
  useTrackSearch({ vehiclesQuery, vehicleSearchParams });
  useFilterHelper(vehicleSearchParams);

  useEffect(() => {
    if (data?.query.transformedParameters) {
      const transformed = sanitizeParams(data.query.transformedParameters);
      const currentParams = sanitizeParams(searchParams);

      // Get the current q parameter to preserve the unescaped version
      const currentQ = currentParams.get('q');
      const transformedQ = transformed.get('q');

      // If the backend has escaped the query, don't update
      // Check for common escape patterns like backslashes
      if (transformedQ?.includes('\\')) {
        if (currentQ && !currentQ.includes('\\')) {
          // Backend has escaped the query, keep our original
          transformed.delete('q');
          if (currentQ) {
            transformed.set('q', currentQ);
          }
        }
      }

      // Only update if the parameters have actually changed
      if (transformed.toString() !== currentParams.toString()) {
        updateUrl(transformed, { replace: true });
      }
    }
  }, [data?.query.transformedParameters, searchParams, updateUrl]);

  const totalHits = useMemo(
    () =>
      data?.stats.totalHits !== undefined
        ? data.stats.totalHits
        : initialFacetsByParams?.stats.totalHits
          ? initialFacetsByParams.stats.totalHits
          : (facets?.stats.totalHits ?? 0),
    [data?.stats.totalHits, facets?.stats.totalHits, initialFacetsByParams?.stats.totalHits],
  );

  const seoPathname = pathname.split('/').slice(2)?.join('/') || '';
  const seoId = btoa(seoPathname);

  // Resolve SEO with smart loading state handling
  const { seo, fallbackTitle, fallbackDescription, fallbackMetaTitle, fallbackMetaDescription } =
    useResolvedSeo({
      seoId,
      market,
      searchParams,
      vehicleSearchParams,
      searchSubscriptionId,
      brandName: marketMetadata.brandName || '',
      initialSeo,
      initialDynamicSeo,
    });

  const activeFiltersSummaryLabel = useActiveFiltersSummaryLabel({
    enrichedFacets,
    params: searchParams,
  });

  // Update document metadata on the client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Use the final resolved title for the document
      const baseTitle = seo?.metaTitle || fallbackMetaTitle;
      if (baseTitle) {
        // Apply the same logic as createMetadataTitle
        const titleParts: string[] = [];
        titleParts.push(baseTitle);
        if (marketMetadata.brandName) {
          titleParts.push(marketMetadata.brandName);
        }
        document.title = titleParts.join(' - ');
      }

      // Update meta description
      const metaDescription = seo?.metaDescription || fallbackMetaDescription;
      if (metaDescription) {
        const metaTag = document.querySelector('meta[name="description"]');
        if (metaTag) {
          metaTag.setAttribute('content', metaDescription);
        }
      }
    }
  }, [
    seo?.metaTitle,
    seo?.metaDescription,
    fallbackTitle,
    fallbackDescription,
    marketMetadata.brandName,
  ]);

  const onLinkClick = useCallback(() => {
    const subscribeableQuery = getSubscribeableQueryForVehicleSearch(searchParams);
    if (subscribeableQuery.toString()) {
      addFilterHistory.mutate({
        id: v4(),
        query: subscribeableQuery.toString(),
        label: activeFiltersSummaryLabel,
        date: new Date().toISOString(),
      });
    }
  }, [searchParams, activeFiltersSummaryLabel]);

  return (
    <PageContent pageType="contentPage">
      <SearchHeader
        searchSubscriptionId={searchSubscriptionId}
        heading={seo?.title ?? fallbackTitle}
        label={searchSubscriptionId ? tSearchSubscription('label') : undefined}
        description={
          seo?.ingress ? (
            <TextContent
              limitWidth
              textSize="md"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(seo.ingress) }}
            />
          ) : (
            <TextContent limitWidth textSize="md">
              <p>{fallbackDescription}</p>
            </TextContent>
          )
        }
      />
      <SearchControls>
        {false && (
          <SearchInput
            rootUrl={rootUrl}
            searchPage={searchPage}
            searchParams={searchParams}
            data={data}
            isFetching={!isPolling && vehiclesQuery.isFetching}
          />
        )}
        <SearchFilters
          enrichedFacets={enrichedFacets}
          loading={!isPolling && (vehiclesQuery.isFetching || searchSubscriptionQuery.isFetching)}
          totalHits={totalHits}
          market={market}
          rootUrl={rootUrl}
        />
        <ActiveFilters
          q={searchParams.get('q') ?? undefined}
          enrichedFacets={enrichedFacets}
          loading={!isPolling && (vehiclesQuery.isFetching || searchSubscriptionQuery.isFetching)}
          rootUrl={rootUrl}
          totalHits={totalHits}
        />
        <SearchSubscription
          rootUrl={rootUrl}
          searchSubscriptionId={searchSubscriptionId}
          totalHits={totalHits}
        />
        <ManufacturerPackagingSuggestion searchParams={searchParams} rootUrl={rootUrl} />
        <UtilityBar
          label={t('meta', {
            value: totalHits,
          })}
        >
          <FilterSort rootUrl={rootUrl} sortOptions={facets?.documentList.sortOptions} />
        </UtilityBar>
        <SearchResult
          data={data}
          query={vehiclesQuery}
          searchSubscriptionQuery={searchSubscriptionQuery}
          itemPage={itemPage}
          currency={currency}
          onLinkClick={onLinkClick}
          isPolling={isPolling}
        />
      </SearchControls>
      <Pagination pagination={data?.documentList.pagination} rootUrl={rootUrl} />
      <SearchLinkCloud
        searchPage={searchPage}
        linkCloudList={linkCloudList}
        facets={facets}
        marketMetadata={marketMetadata}
        currentFilters={{
          manufacturer: searchParams.get('manufacturer') ?? undefined,
          modelSeries: searchParams.get('modelSeries') ?? undefined,
          allManufacturers: searchParams.getAll('manufacturer'),
        }}
      />
    </PageContent>
  );
};

export default SearchContainer;
